import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { ArchivedDocument } from '@/types/domain'

/* The paper archived against one case, for a reader who may not see Finance's
 * side of it.
 *
 * `GET /api/documents?sourceKind=case&sourceId=…` lists every kind archived
 * against a reachable case to any holder of `document.archive.read` — all four
 * seeded roles — after the case's row scope. It carries existence only (kind,
 * number, dates, the entity's trading name), which is why routes.go mounts it
 * with no role guard: "an Operations user running a shipment should be able to
 * tell that an invoice went out". The bytes are a separate, per-kind question
 * (`mayOpenArchivedKind`).
 *
 * Finance's own workspace does NOT read this: its invoices and debit notes come
 * from `/cases/:id/invoices` and `/cases/:id/debit-notes` (`useCaseDocuments`),
 * which carry the amounts this list deliberately does not.
 */

/** Which archive rows are DOCUMENTS, and in what order.
 *
 *  An invoice or debit-note row that is not issued is a render leftover, not a
 *  piece of paper: every Generate inserts an archive row, and an edit after it
 *  orphans the previous one (the finance row's `documentId` is cleared, the
 *  archive row stays). Listing them would show Operations three "drafts" of one
 *  invoice that Finance is still typing. So those two kinds are shown issued
 *  only.
 *
 *  A status update is never issued — it has no number and its archive row is
 *  the whole of it; only those filed before S-15 exist, since the send now
 *  records itself in the status send journal instead — and an offer filed
 *  against the case (migration 0037's
 *  legacy status updates were filed as `offer`) may not be either; both are
 *  always shown, and the view puts no draft badge on them.
 *
 *  Newest first by when the paper was issued, or else rendered. RFC 3339 in UTC
 *  compares correctly as a string, so no date is parsed here. */
export function caseArchiveRows(items: ArchivedDocument[]): ArchivedDocument[] {
  return items
    .filter(
      (item) => item.issued || (item.kind !== 'invoice' && item.kind !== 'debit-note'),
    )
    .sort((a, b) => stampOf(b).localeCompare(stampOf(a)))
}

const stampOf = (item: ArchivedDocument) => item.issuedAt ?? item.renderedAt

/** The case's archive list, filtered and ordered by `caseArchiveRows`.
 *
 *  `enabled` is the caller's capability answer: a reader without
 *  `document.archive.read` would get a 403, and the view that calls this is
 *  not rendered for them anyway — the flag keeps a deep link from asking.
 *
 *  Keyed under `cases.detail(id)` (`queryKeys.cases.archive`), so any write
 *  that invalidates the case reaches it. (The status send no longer adds a row
 *  here — S-15 retired its PDF for the case's status send journal — though it
 *  still invalidates the case.) The raw rows are cached and the filter runs in
 *  `select`, so the cache holds what the server said.
 *
 *  A 404 is not "no paper": the archive is mounted only where object storage
 *  is configured, and the caller says so in its own words. The global retry
 *  rule already declines to retry a 4xx. */
export function useCaseArchive(caseId: string, enabled: boolean) {
  return useQuery<ArchivedDocument[], Error, ArchivedDocument[]>({
    queryKey: queryKeys.cases.archive(caseId),
    enabled: enabled && caseId !== '',
    queryFn: async () => {
      /* No archive in the fixture build: it has no object storage either, and
         an empty list is the honest answer for a case nothing was rendered for. */
      if (USE_MOCKS) return []
      return (
        await api.get<{ items: ArchivedDocument[] }>('/documents', {
          params: { sourceKind: 'case', sourceId: caseId },
        })
      ).items
    },
    select: caseArchiveRows,
  })
}
